import { formatRate } from '@/lib/format'
import {
  FTWE_TIER_LABELS,
  RATE_FIELDS,
  type FtweTier,
  type RateCardChange,
  type RateChange,
  type RateField,
} from '../types'

/** A rate card value as the billing page shows it: the minute rate in
 *  dollars, a discount or multiplier as a plain number. Relay sends each as a
 *  numeric(10,4) string, so 0.5000 reads as $0.50 or 0.5. */
export function formatRateField(key: RateField, value: string): string {
  return key === 'minute_rate' || key === 'document_rate'
    ? formatRate(Number(value))
    : String(Number(value))
}

/** The FTWE setting as the billing page shows it: its tier and the minutes
 *  per person-day it stands for, e.g. "Med (191)". Relay sends the minutes as
 *  a numeric(10,4) string, so 191.0000 reads as 191. */
export function formatFtwe(
  tier: FtweTier,
  multiplier: string | number
): string {
  return `${FTWE_TIER_LABELS[tier]} (${Number(multiplier)})`
}

/** The rates a change moves, compared as numbers so 0.5500 to 0.55 is no
 *  move. */
export function movedRates(change: RateCardChange) {
  return RATE_FIELDS.filter(
    ({ key }) => Number(change.current[key]) !== Number(change.proposed[key])
  )
}

/** Whether a change moves the FTWE setting: its tier, or its minutes. */
export function ftweMoved(change: RateCardChange): boolean {
  return (
    change.ftwe_current.tier !== change.ftwe_proposed.tier ||
    Number(change.ftwe_current.multiplier) !==
      Number(change.ftwe_proposed.multiplier)
  )
}

/** Whether a change moves anything at all; one that does not has nothing to
 *  apply. */
export function changeMovesSomething(change: RateCardChange): boolean {
  return movedRates(change).length > 0 || ftweMoved(change)
}

const RATE_CHANGE_NAMES: Record<RateField, string> = {
  minute_rate: 'minute rate',
  non_conversation_discount: 'non-conversation discount',
  reasoning_multiplier: 'reasoning multiplier',
  computer_use_multiplier: 'computer-use multiplier',
  david_session_multiplier: 'David-session multiplier',
  document_rate: 'Document rate',
  rubric_eval_multiplier: 'rubric-evaluation multiplier',
}

/** A sentence per day the rates changed, saying what each moved from and to. */
export function rateChangeSentences(changes: RateChange[]): string[] {
  const byDay = new Map<string, RateChange[]>()
  for (const change of changes) {
    byDay.set(change.effective_from, [
      ...(byDay.get(change.effective_from) ?? []),
      change,
    ])
  }
  return [...byDay].map(([effectiveFrom, moved]) => {
    const day = new Date(effectiveFrom).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    })
    const parts = moved.map(
      ({ field, before, after }) =>
        `${RATE_CHANGE_NAMES[field]} ${formatRateField(field, before)} → ${formatRateField(field, after)}`
    )
    return `Rates changed on ${day} (UTC): ${parts.join(', ')}.`
  })
}
